/**
 * Is the patient following their plan? Two pure read models for the oncologist's dashboard:
 *
 *   goalStatus      each care goal, this week, as target vs actual and met / missed / not enough data
 *   recentActivity  the last things the patient did, as short sentences
 */

import { describeSymptom, metricDays, todayTotal, weeklyProgress } from './derive.js';
import { dayKey } from './time.js';
import { METRIC_META } from './tracks.js';
import type { CareEvent, CareGoal, PatientState } from './types.js';

export interface GoalStatus {
  kind: CareGoal['kind'];
  label: string;
  /** "every morning", "2,000 ml a day", "95%+". */
  target: string;
  /** "6/7 days", "1,400 ml", "92%". */
  actual: string;
  /** Null when the week holds too little data to say either way. */
  met: boolean | null;
}

/** A step goal counts as met when the target was reached on at least this many of the last 7 days. */
export const STEP_DAYS_TO_MEET = 5;

/** One goal as a title and a target, for anywhere a goal is listed. */
export function goalLabel(g: CareGoal): { title: string; target: string } {
  switch (g.kind) {
    case 'daily-temperature':
      return { title: 'Daily temperature', target: g.daysPerWeek >= 7 ? 'every morning' : `on ${g.daysPerWeek}+ mornings` };
    case 'symptom-check':
      return { title: 'Symptom check', target: g.daysPerWeek >= 7 ? 'every day' : `on ${g.daysPerWeek}+ days` };
    case 'adherence':
      return { title: 'Medications taken', target: `${g.percent}%+` };
    case 'steps':
      return { title: 'Daily steps', target: `${g.target.toLocaleString('en-US')} on ${STEP_DAYS_TO_MEET}+ days` };
    case 'intake':
      return { title: 'Fluids', target: `${g.mlPerDay.toLocaleString('en-US')} ml a day` };
    case 'weekly-weight':
      return { title: 'Weight', target: `on ${g.daysPerWeek}+ ${g.daysPerWeek === 1 ? 'morning' : 'mornings'} a week` };
  }
}

export function goalStatus(state: PatientState, now: number): GoalStatus[] {
  const p = weeklyProgress(state, now);
  return state.goals.map((g): GoalStatus => {
    const { title, target } = goalLabel(g);
    switch (g.kind) {
      case 'daily-temperature':
        return {
          kind: g.kind,
          label: title,
          target,
          actual: `${p.temperature.done}/7 days`,
          met: state.readings.length === 0 ? null : p.temperature.done >= g.daysPerWeek,
        };
      case 'symptom-check':
        return {
          kind: g.kind,
          label: title,
          target,
          actual: `${p.symptomChecks.done}/7 days`,
          met: state.symptomReports.length === 0 ? null : p.symptomChecks.done >= g.daysPerWeek,
        };
      case 'adherence':
        return {
          kind: g.kind,
          label: title,
          target,
          actual: p.adherence.scheduled === 0 ? 'no doses due' : `${p.adherence.percent}%`,
          met: p.adherence.scheduled === 0 ? null : p.adherence.percent >= g.percent,
        };
      case 'steps':
        return {
          kind: g.kind,
          label: title,
          target,
          actual: `${p.steps.daysComplete}/7 days`,
          met: state.steps.length === 0 ? null : p.steps.daysComplete >= STEP_DAYS_TO_MEET,
        };
      case 'intake': {
        const today = todayTotal(state, 'intake-ml', now);
        return {
          kind: g.kind,
          label: title,
          target,
          actual: today === null ? 'nothing logged today' : `${today.toLocaleString('en-US')} ml today`,
          met: today === null ? null : today >= g.mlPerDay,
        };
      }
      case 'weekly-weight': {
        const days = metricDays(state, 'weight-lbs', now).filter((m) => m.manual === true).length;
        return { kind: g.kind, label: title, target, actual: `${days}/7 days`, met: days === 0 ? null : days >= g.daysPerWeek };
      }
    }
  });
}

export interface ActivityItem {
  at: number;
  kind: CareEvent['type'];
  text: string;
}

/** One event as a sentence about the patient, or null for events too noisy to list (device samples). */
export function describeEvent(ev: CareEvent, state: PatientState | null): ActivityItem | null {
  switch (ev.type) {
    case 'enroll':
      return { at: ev.at, kind: ev.type, text: 'Enrolled in OncoCoach' };
    case 'update-plan':
      return { at: ev.at, kind: ev.type, text: 'Treatment plan updated' };
    case 'start-cycle':
      return { at: ev.at, kind: ev.type, text: `Started cycle ${ev.number}` };
    case 'log-temperature': {
      const symptoms = (ev.symptoms ?? []).filter((s) => s.grade > 0).map(describeSymptom);
      return { at: ev.at, kind: ev.type, text: `Logged ${ev.celsius.toFixed(1)} °C${symptoms.length ? ` with ${symptoms.join(' and ').toLowerCase()}` : ''}` };
    }
    case 'log-symptoms': {
      const names = ev.symptoms.filter((s) => s.grade > 0).map(describeSymptom);
      return { at: ev.at, kind: ev.type, text: names.length > 0 ? `Reported ${names.join(' and ').toLowerCase()}` : 'Checked in, nothing to report' };
    }
    case 'take-med': {
      const med = state?.medications.find((m) => m.id === ev.medicationId);
      return { at: ev.at, kind: ev.type, text: `Took ${med?.name ?? ev.medicationId}` };
    }
    case 'steps':
      return { at: ev.at, kind: ev.type, text: `${ev.count.toLocaleString('en-US')} steps` };
    case 'log-performance':
      return { at: ev.at, kind: ev.type, text: `Rated their day ECOG ${ev.score}` };
    case 'coach-checkin':
      return { at: ev.at, kind: ev.type, text: 'Checked in with the coach' };
    case 'wear-device':
      return { at: ev.at, kind: ev.type, text: 'Wore their device all day' };
    case 'metric': {
      // A watch sends hundreds of these a day; a weight the patient stepped on the scale for is an act.
      if (ev.source !== 'manual') return null;
      const meta = METRIC_META[ev.kind];
      return { at: ev.at, kind: ev.type, text: `${meta.label} ${ev.value.toFixed(meta.decimals)} ${meta.unit}` };
    }
  }
}

/**
 * The most recent activity, newest first. Consecutive doses on the same day collapse into one line,
 * so a morning of five pills reads as "Took 5 doses" rather than filling the list.
 */
export function recentActivity(events: readonly CareEvent[], state: PatientState | null, limit = 8): ActivityItem[] {
  const tz = state?.profile.tzOffsetMinutes ?? 0;
  // The log is append-ordered; an imported or seeded day need not be in clock order.
  const sorted = events.map((e, i) => ({ e, i })).sort((a, b) => a.e.at - b.e.at || a.i - b.i);
  const out: Array<ActivityItem & { n?: number }> = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const item = describeEvent((sorted[i] as { e: CareEvent }).e, state);
    if (!item) continue;
    const last = out[out.length - 1];
    if (last && item.kind === 'take-med' && last.kind === 'take-med' && dayKey(last.at, tz) === dayKey(item.at, tz)) {
      last.n = (last.n ?? 1) + 1;
      last.text = `Took ${last.n} doses`;
      continue;
    }
    if (out.length >= limit) break;
    out.push({ ...item });
  }
  return out.map(({ n: _n, ...rest }) => rest);
}
